import React, { useEffect, useMemo, useState } from 'react';
import { FaGoogle } from 'react-icons/fa';
import {
  API_BASE_URL,
  OAUTH_RETURN_URL_KEY,
} from '../../utils/mindmap/apiClient';

const AuthDialog = ({ isOpen, onClose, onAuthSuccess, initialMode = 'signin' }) => {
  const isDarkMode = useMemo(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('mindMapTheme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
    } catch {
      // ignore
    }
    return true;
  }, []);

  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  const [displayName, setDisplayName] = useState('');
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [betaStatus, setBetaStatus] = useState(null);
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setError('');
    setWaitlistJoined(false);
    fetch(`${API_BASE_URL}/beta/status`)
      .then((response) => response.ok ? response.json() : null)
      .then(setBetaStatus)
      .catch(() => setBetaStatus(null));
  }, [initialMode, isOpen]);

  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 210,
  };

  const dialogStyle = {
    minWidth: 320,
    maxWidth: 'calc(100% - 100px)',
    borderRadius: 12,
    padding: '18px 50px 16px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.45)',
    backgroundColor: isDarkMode
      ? 'rgba(45, 45, 45, 0.9)'
      : 'rgba(255, 255, 255, 0.9)',
    color: isDarkMode ? '#f9fafb' : '#111827',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  const headerStyle = {
    marginBottom: 20,
    fontSize: 20,
    fontWeight: 600,
    textAlign: 'center',
  };

  const subTextStyle = {
    marginBottom: 16,
    fontSize: 13,
    lineHeight: 1.4,
    color: isDarkMode ? '#d1d5db' : '#4b5563',
  };

  const buttonRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
  };

  const providerButtonStyle = {
    width: '100%',
    borderRadius: 999,
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    fontSize: 18,
    cursor: 'pointer',
    backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    color: isDarkMode ? '#f9fafb' : '#111827',
    textAlign: 'center',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const providerContentStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  };

  const providerIconStyle = {
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  };

  const googleIconStyle = {
    ...providerIconStyle,
    color: '#ea4335',
  };

  /*
  const githubIconStyle = {
    ...providerIconStyle,
    color: isDarkMode ? '#f9fafb' : '#111827',
  };
  */

  const formRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 16,
  };

  const inputStyle = {
    width: '95%',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    padding: '15px 10px',
    textAlign: 'center',
    fontSize: 15,
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.9)' : 'rgba(255, 255, 255, 0.9)',
    color: isDarkMode ? '#f9fafb' : '#111827',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const signInButtonStyle = {
    borderRadius: 999,
    border: 'none',
    padding: '15px 10px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: 15,
    cursor: 'pointer',
    backgroundColor: '#2563eb',
    color: '#f9fafb',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const secondaryRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
  };



  const textButtonStyle = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    fontSize: 15,
    marginLeft: 15,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    cursor: 'pointer',
  };

  const closeButtonStyle = {
    borderRadius: 999,
    border: 'none',
    padding: '4px 10px',
    fontSize: 13,
    cursor: 'pointer',
    backgroundColor: isDarkMode ? '#1f2937' : '#f3f4f6',
    color: isDarkMode ? '#e5e7eb' : '#111827',
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const handleGoogleSignIn = () => {
    try {
      const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.sessionStorage.setItem(OAUTH_RETURN_URL_KEY, returnUrl);
    } catch {
      // The user can still sign in; only return-to-map convenience is lost.
    }
    // Redirect-based OAuth: send the browser to backend start endpoint
    window.location.href = `${API_BASE_URL}/auth/oauth/google/start`;
  };

  const handleEmailSubmit = async () => {
    setError('');
    if (isSubmitting) return;
    const email = emailOrUsername.trim();

    // Basic client-side validation
    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }

    const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (mode === 'signup') {
      if (!displayName.trim()) {
        setError('Please enter a display name.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
    }

    const endpoint = mode === 'signup' ? '/auth/register' : '/auth/login';
    const body =
      mode === 'signup'
        ? { email, password, displayName: displayName.trim() || null }
        : { email, password };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (data && data.code === 'BETA_FULL') {
          setMode('waitlist');
          setError('');
          return;
        }
        const message = (data && data.error) || 'Authentication failed.';
        setError(message);
        return;
      }

      const data = await res.json();
      if (data && data.token && onAuthSuccess) {
        onAuthSuccess(data.token);
      }
      if (onClose) onClose();
    } catch (e) {
      console.error('Email auth failed', e);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWaitlistSubmit = async () => {
    setError('');
    if (isSubmitting) return;
    const email = emailOrUsername.trim();
    const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailPattern.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/beta/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(`Waitlist request failed: ${response.status}`);
      setWaitlistJoined(true);
    } catch {
      setError('We could not save your email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-dialog-title"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div id="auth-dialog-title" style={headerStyle}>
          {mode === 'signin'
            ? 'Sign in to Mindmappi'
            : mode === 'signup'
              ? 'Create your Mindmappi account'
              : 'Join the Mindmappi beta waitlist'}
        </div>

        <div style={subTextStyle}>
          {mode === 'waitlist'
            ? waitlistJoined
              ? 'You’re on the list. We’ll email you when more beta places become available.'
              : 'The private beta is currently full. Leave your email and we’ll let you know when more places open.'
            : betaStatus && `${betaStatus.remaining} of ${betaStatus.capacity} private-beta places remain.`}
        </div>

        {mode !== 'waitlist' && <div style={buttonRowStyle}>
          <button
            type="button"
            style={providerButtonStyle}
            onClick={handleGoogleSignIn}
          >
            <span style={providerContentStyle}>
              <span style={googleIconStyle}>
                <FaGoogle />
              </span>
              <span>Sign in with Google</span>
            </span>
          </button>
          {/* Future feature: GitHub OAuth sign-in */}
          {/*
          <button
            type="button"
            style={providerButtonStyle}
            onClick={() => {
              console.log('Continue with GitHub (not wired yet)');
            }}


            <span style={providerContentStyle}>
              <span style={githubIconStyle}>
                <FaGithub />
              </span>
              <span>Sign in with GitHub</span>
            </span>
          </button>
          */}
        </div>}
        {mode !== 'waitlist' && <div style={{ textAlign: 'center', margin: '20px 0' }}>Or</div>}
        <div style={formRowStyle}>
          {mode === 'signup' && (
            <input
              type="text"
              aria-label="Display name"
              style={inputStyle}
              placeholder="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          )}
          <input
            type="text"
            aria-label="Email"
            style={inputStyle}
            placeholder="Email"
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
          />
          {mode !== 'waitlist' && (
            <input
              type="password"
              aria-label="Password"
              style={inputStyle}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
          <button
            type="button"
            style={signInButtonStyle}
            onClick={mode === 'waitlist' ? handleWaitlistSubmit : handleEmailSubmit}
            disabled={isSubmitting || (mode === 'waitlist' && waitlistJoined)}
          >
            {mode === 'waitlist'
              ? waitlistJoined
                ? 'Email saved'
                : isSubmitting ? 'Joining...' : 'Notify me'
              : isSubmitting
              ? mode === 'signin'
                ? 'Signing in...'
                : 'Signing up...'
              : mode === 'signin'
                ? 'Sign in'
                : 'Sign up'}
          </button>
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5', textAlign: 'center' }}>
              {error}
            </div>
          )}
        </div>
        <div style={secondaryRowStyle}>
          <button
            type="button"
            style={textButtonStyle}
            onClick={() => {
              setMode((prev) => (prev === 'signin' ? 'signup' : 'signin'));
              setError('');
            }}
          >
            {mode === 'signin'
              ? 'New here? Create an account'
              : 'Already have an account? Sign in'}
          </button>
          <button type="button" style={closeButtonStyle} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div></div>
    </div>
  );
};

export default AuthDialog;
